<template>
  <div v-if="show" class="vendor-transactions-modal-overlay" @click.self="handleClose">
    <div class="vendor-transactions-modal-content">
      <div class="vendor-transactions-modal-header">
        <div class="vendor-transactions-modal-title-wrapper">
          <i class="bi bi-receipt vendor-transactions-modal-title-icon"></i>
          <h3>{{ title }}</h3>
        </div>
        <button class="vendor-transactions-modal-close" @click="handleClose">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="vendor-transactions-modal-body">
        <p class="vendor-transactions-modal-description">{{ description }}</p>
        
        <div v-if="loading" class="vendor-transactions-loading">
          <i class="bi bi-arrow-repeat bi-spin"></i>
          <span>Loading transactions...</span>
        </div>
        
        <div v-else-if="error" class="vendor-transactions-error">
          <i class="bi bi-exclamation-triangle"></i>
          <span>{{ error }}</span>
        </div>
        
        <div v-else-if="transactions.length === 0" class="vendor-transactions-empty">
          <i class="bi bi-inbox"></i>
          <span>No transactions found for selected vendors</span>
        </div>
        
        <div v-else class="vendor-transactions-content">
          <div class="vendor-transactions-summary">
            <div class="vendor-transactions-summary-item">
              <span class="summary-label">Total Transactions:</span>
              <span class="summary-value">{{ transactions.length }}</span>
            </div>
            <div class="vendor-transactions-summary-item">
              <span class="summary-label">Total Amount:</span>
              <span class="summary-value">{{ formatCurrency(totalAmount) }}</span>
            </div>
          </div>
          
          <div class="vendor-transactions-table-wrapper">
            <table class="vendor-transactions-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Type</th>
                  <th>Number</th>
                  <th>Date</th>
                  <th>Memo</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th>Account</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(transaction, index) in paginatedTransactions" :key="`${transaction.id}-${index}`">
                  <td>{{ getVendorName(transaction.vendorId) }}</td>
                  <td>
                    <span class="transaction-type-badge" :class="getTransactionTypeClass(transaction.type)">
                      {{ transaction.type }}
                    </span>
                  </td>
                  <td>{{ transaction.number }}</td>
                  <td>{{ formatDate(transaction.date) }}</td>
                  <td class="memo-cell" :title="transaction.memo">{{ transaction.memo || '-' }}</td>
                  <td>{{ formatDate(transaction.dueDate) }}</td>
                  <td>{{ formatCurrency(transaction.amount) }}</td>
                  <td>{{ formatAccount(transaction.account) }}</td>
                  <td>
                    <span class="transaction-status-badge" :class="getStatusClass(transaction.status)">
                      {{ transaction.status || '-' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Pagination -->
          <div v-if="totalPages > 1" class="vendor-transactions-pagination">
            <button 
              class="pagination-btn" 
              :disabled="currentPage === 1"
              @click="goToPage(currentPage - 1)"
            >
              <i class="bi bi-chevron-left"></i> Previous
            </button>
            <span class="pagination-info">
              Page {{ currentPage }} of {{ totalPages }} ({{ transactions.length }} transactions)
            </span>
            <button 
              class="pagination-btn" 
              :disabled="currentPage === totalPages"
              @click="goToPage(currentPage + 1)"
            >
              Next <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="vendor-transactions-modal-footer">
        <button class="vendor-transactions-btn vendor-transactions-btn-secondary" @click="handleClose">
          Cancel
        </button>
        <button 
          class="vendor-transactions-btn vendor-transactions-btn-primary" 
          :disabled="loading || transactions.length === 0"
          @click="handleExport"
        >
          <i class="bi bi-file-earmark-excel"></i>
          Export to Excel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import './VendorTransactionsModal.css';

export default {
  name: 'VendorTransactionsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Vendor Transactions'
    },
    description: {
      type: String,
      default: 'Review vendor transactions before exporting to Excel'
    },
    transactions: {
      type: Array,
      default: () => []
    },
    vendors: {
      type: Array,
      default: () => []
    },
    accountsMap: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'export'],
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 20
    };
  },
  computed: {
    totalAmount() {
      return this.transactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
    },
    totalPages() {
      return Math.ceil(this.transactions.length / this.itemsPerPage);
    },
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.transactions.slice(start, start + this.itemsPerPage);
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.currentPage = 1;
      }
    }
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    handleExport() {
      this.$emit('export');
    },
    getVendorName(vendorId) {
      const vendor = this.vendors.find(v => 
        String(v.id || v.vendorId) === String(vendorId)
      );
      return vendor?.vendorName || vendor?.name || '-';
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount) || amount === 0) {
        return '-';
      }
      return '$' + Number(amount).toFixed(2);
    },
    formatDate(date) {
      if (!date) return '-';
      try {
        return new Date(date).toLocaleDateString();
      } catch (e) {
        return date;
      }
    },
    formatAccount(accountId) {
      if (!accountId) return '-';
      if (!this.accountsMap || typeof this.accountsMap !== 'object') return accountId;
      
      const acc = this.accountsMap[accountId];
      if (!acc) return accountId || '-';
      
      try {
        return [
          acc.accountNumber,
          acc.accountName,
          acc.accountType
        ].filter(Boolean).join(' - ') || accountId || '-';
      } catch (error) {
        return accountId || '-';
      }
    },
    getTransactionTypeClass(type) {
      const typeMap = {
        'Bill': 'type-bill',
        'Pay Bill': 'type-pay-bill',
        'Cash Purchase': 'type-cash-purchase',
        'Cheque': 'type-cheque',
        'Vendor Credit Note': 'type-vendor-credit-note',
        'Expense': 'type-expense'
      };
      return typeMap[type] || 'type-default';
    },
    getStatusClass(status) {
      if (!status) return 'status-default';
      const statusLower = status.toLowerCase();
      if (statusLower.includes('paid') || statusLower.includes('received')) {
        return 'status-success';
      } else if (statusLower.includes('overdue') || statusLower.includes('unpaid')) {
        return 'status-danger';
      } else if (statusLower.includes('draft') || statusLower.includes('pending')) {
        return 'status-warning';
      }
      return 'status-default';
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    }
  }
};
</script>

